/**
 * Code block with syntax highlighting and copy button.
 * Special-cases language='mermaid' to render a live diagram, with
 * line-truncation matching mobile (message_bubble.dart _CodeBlockBuilder)
 * so each completed source line renders progressively as deltas land.
 */
import React, { useState, useEffect, useRef } from 'react';
import hljs from 'highlight.js/lib/core';
import MermaidBlock from './MermaidBlock';

import javascript from 'highlight.js/lib/languages/javascript';
import python from 'highlight.js/lib/languages/python';
import bash from 'highlight.js/lib/languages/bash';
import json from 'highlight.js/lib/languages/json';
import css from 'highlight.js/lib/languages/css';
import xml from 'highlight.js/lib/languages/xml';
import typescript from 'highlight.js/lib/languages/typescript';
import sql from 'highlight.js/lib/languages/sql';
import java from 'highlight.js/lib/languages/java';
import cpp from 'highlight.js/lib/languages/cpp';
import go from 'highlight.js/lib/languages/go';
import rust from 'highlight.js/lib/languages/rust';
import yaml from 'highlight.js/lib/languages/yaml';
import markdown from 'highlight.js/lib/languages/markdown';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sh', bash);
hljs.registerLanguage('json', json);
hljs.registerLanguage('css', css);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('java', java);
hljs.registerLanguage('cpp', cpp);
hljs.registerLanguage('c', cpp);
hljs.registerLanguage('go', go);
hljs.registerLanguage('rust', rust);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('yml', yaml);
hljs.registerLanguage('markdown', markdown);
hljs.registerLanguage('md', markdown);

const CodeBlock = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  const codeRef = useRef(null);
  const isMermaid = (language || '').toLowerCase() === 'mermaid';

  useEffect(() => {
    if (isMermaid) return;
    if (!codeRef.current || !language) return;
    // hljs.highlight() emits a console warning for any unregistered language
    // (e.g. csv) from inside the call, before it returns — so a try/catch
    // cannot suppress it. Guard with getLanguage: an unsupported language
    // renders as plain text, no warning.
    if (!hljs.getLanguage(language)) {
      codeRef.current.textContent = code;
      return;
    }
    try {
      const result = hljs.highlight(code, { language, ignoreIllegals: true });
      codeRef.current.innerHTML = result.value;
    } catch {
      codeRef.current.textContent = code;
    }
  }, [code, language, isMermaid]);

  if (isMermaid) return <MermaidBlock code={code} />;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="relative group rounded-lg overflow-hidden my-2 not-prose" style={{ background: '#0d1117' }}>
      {language && (
        <div className="absolute top-2 left-3 text-xs uppercase tracking-wider" style={{ color: '#8b949e' }}>{language}</div>
      )}
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded"
        style={{ color: '#8b949e', zIndex: 10 }}
        aria-label="Copy code"
      >
        {copied ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
      <pre className="overflow-x-auto p-4 pt-9 text-sm" style={{ margin: 0 }}>
        <code ref={codeRef} className="font-mono" style={{ color: '#e6edf3' }}>
          {language ? null : code}
        </code>
      </pre>
    </div>
  );
};

export default React.memo(CodeBlock);
